/**
 * Apartsales — AppShell (app-first architecture).
 *
 * Apartsales is primarily a native iOS / Android app. The web build is the
 * SAME app rendered in a phone-shaped frame on desktop.
 *
 * Mobile (≤520px): phone frame disappears, app fills viewport edge-to-edge,
 *   uses 100dvh (dynamic viewport height) so it adapts when Safari's bottom
 *   chrome shows/hides during scroll.
 * Desktop (>520px): 390×844 logical phone frame, centered on dark canvas.
 *
 * Scroll architecture (the bug fixes):
 *   1. Each shell owns its own scroll container via useRef
 *   2. useScrollDirection takes a RefObject — listens directly on that el,
 *      no document.querySelector (which would find the FIRST scroll-root
 *      in DOM, i.e. PhoneFrame's main even when display:none on mobile)
 *   3. Header is wrapped in a collapsing div that animates max-height 48→0,
 *      so when hidden the layout space ALSO collapses (translateY alone
 *      doesn't change layout — that's what caused the "black strip" bug)
 */

import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from '@tanstack/react-router';
import { TopHeader } from './TopHeader';
import { TabBar } from './TabBar';
import { ToastHost } from './toast';
import { VISIBILITY } from '~/lib/visibility';

interface AppShellProps {
  children: ReactNode;
}

/**
 * Routes that opt out of the global TopHeader (apartsales/photo/bag).
 * The TabBar still renders. Used by screens with their own custom top
 * chrome — currently /property/* (PropertyDetail has its own back+share+heart
 * floating bar over the photo carousel).
 *
 * Когда PROPERTY_DETAIL_CONTENT выключен, PropertyDetail не рендерится, и
 * /property/* должен показывать обычный TopHeader. Поэтому массив пуст.
 */
/**
 * Routes that should render without the global TopHeader. These pages have
 * their own top chrome:
 *   - /property/* — legacy PropertyDetail с floating action bar (only if VISIBILITY flag on)
 *   - /unit/* — legacy UUID URL → redirect к slug
 *   - /{country}/{city}/{developer}/{complex}/unit/{unit} — SEO slug URL (§2.4 ТЗ)
 */
const HEADERLESS_PREFIXES: readonly string[] = [
  ...(VISIBILITY.PROPERTY_DETAIL_CONTENT ? ['/property'] : []),
  '/unit',
];

/**
 * Регулярка для SEO slug-URL юнита (новая 8-сегментная схема, см. docs/07-URL-ARCHITECTURE-v1.md):
 *   /russia/krasnodar-krai/sochi/adler/ug-development/morskoy-briz/studio/0k-28m-a-08-101
 * Для federal subjects (Москва/СПб/Севастополь) — 7 сегментов без региона:
 *   /russia/moscow/khamovniki/pik/zilart/2k/2k-65m-pik-12
 *
 * Структура: 7 или 8 lowercase segments из [a-z0-9-]. Финальный resolver
 * в slug-routing-v2 точно проверит юнит в БД; здесь нужно только определить
 * «это похоже на юнит-URL» для скрытия TabBar — false-positives безопасны
 * (TabBar просто не появится на странице 404 с такой формой).
 */
const UNIT_SLUG_URL_REGEX = /^(?:\/[a-z0-9-]+){7,8}\/?$/;

function isUnitDetailPath(pathname: string): boolean {
  return HEADERLESS_PREFIXES.some((p) => pathname.startsWith(p)) ||
         UNIT_SLUG_URL_REGEX.test(pathname);
}

/**
 * Routes that should render without the bottom TabBar. Full-screen detail
 * pages: TabBar would conflict with the page's own bottom Sticky CTA Bar.
 */
const TABBARLESS_PREFIXES: readonly string[] = ['/unit'];

function isTabbarless(pathname: string): boolean {
  return TABBARLESS_PREFIXES.some((p) => pathname.startsWith(p)) ||
         UNIT_SLUG_URL_REGEX.test(pathname);
}

export function AppShell({ children }: AppShellProps) {
  return (
    <div className="min-h-screen w-full bg-bg text-label">
      {/* Desktop canvas: dark backdrop, soft glow, phone frame centered */}
      <div className="relative hidden min-h-screen w-full items-center justify-center px-4 py-8 sm:flex">
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div
            className="absolute left-1/2 top-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[120px]"
            style={{ backgroundColor: 'rgba(42,150,251,0.08)' }}
          />
        </div>

        <PhoneFrame>{children}</PhoneFrame>
      </div>

      {/* Mobile: native fullscreen with dvh for Safari chrome */}
      <div className="sm:hidden">
        <NativeShell>{children}</NativeShell>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  useScrollDirection — ref-based, one listener per shell         */
/* ────────────────────────────────────────────────────────────── */

interface ScrollState {
  scrolled: boolean;
  hidden: boolean;
}

function useScrollDirection(scrollRef: RefObject<HTMLElement | null>): ScrollState {
  const [state, setState] = useState<ScrollState>({ scrolled: false, hidden: false });
  const lastY = useRef(0);
  /* Сворачивание/разворачивание шапки анимирует её height (48↔0).
     Шапка — в потоке flex-колонки, поэтому анимация рефлоует <main>,
     что синхронно порождает scroll-события. Реакция на них переворачивает
     `hidden` обратно → шапка дёргается «туда-сюда» (бесконечный цикл,
     особенно на короткой странице вроде формы заявки). Решение: на время
     анимации (чуть дольше 220ms) игнорируем scroll-события — цикл рвётся. */
  const lockedUntil = useRef(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    lastY.current = el.scrollTop;

    const onScroll = () => {
      const y = el.scrollTop;
      const now = performance.now();

      // Окно блокировки: рефлоу от анимации шапки не должен влиять на
      // определение направления. Просто синхронизируем lastY и выходим.
      if (now < lockedUntil.current) {
        lastY.current = y;
        return;
      }

      const delta = y - lastY.current;

      // Deadzone — sub-pixel jitter on iOS bounce / trackpad inertia
      if (Math.abs(delta) < 6) return;

      let nextHidden = false;
      if (delta > 0 && y > 60) nextHidden = true;
      else if (delta < 0) nextHidden = false;
      if (y <= 8) nextHidden = false;

      const nextScrolled = y > 8;
      lastY.current = y;

      setState((prev) => {
        if (prev.hidden === nextHidden && prev.scrolled === nextScrolled) return prev;
        // Смена `hidden` запускает анимацию height + рефлоу <main>.
        // Блокируем scroll-driven обновления, пока анимация не осядет.
        if (prev.hidden !== nextHidden) {
          lockedUntil.current = performance.now() + 260;
        }
        return { scrolled: nextScrolled, hidden: nextHidden };
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  return state;
}

/* ────────────────────────────────────────────────────────────── */
/*  CollapsingHeader — wraps TopHeader, animates height + opacity   */
/*                                                                  */
/*  Bug history: первая версия анимировала только `max-height` 0→48 */
/*  с `overflow: hidden`. Во время transition (220ms) был виден     */
/*  middle-frame: иконки и wordmark обрезаны горизонтальной полосой */
/*  (`items-center` в h-12 → центр overlay = центр чрезмерно        */
/*  обрезанных half-icons). UX-эффект: header «дёргается».          */
/*                                                                  */
/*  Fix: одновременная анимация `height` (для пересчёта layout main */
/*  flex) + `opacity` (для плавного исчезновения контента). При     */
/*  height=24 opacity≈0.5 → обрезанные иконки полупрозрачны и       */
/*  читаются как затухание, а не как crop. На fast scroll header    */
/*  unmounts через AnimatePresence — отсутствие layout shift во     */
/*  время unmount.                                                  */
/* ────────────────────────────────────────────────────────────── */

const HEADER_HEIGHT = 48;

function CollapsingHeader({ scrolled, hidden }: { scrolled: boolean; hidden: boolean }) {
  return (
    <AnimatePresence initial={false}>
      {!hidden && (
        <motion.div
          key="top-header"
          className="relative z-30 shrink-0 overflow-hidden"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: HEADER_HEIGHT, opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{
            duration: 0.22,
            ease: [0.2, 0.8, 0.2, 1],
            opacity: { duration: 0.16, ease: 'easeOut' },
          }}
        >
          <TopHeader scrolled={scrolled} hidden={false} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  Phone frame (desktop)                                          */
/* ────────────────────────────────────────────────────────────── */

function PhoneFrame({ children }: { children: ReactNode }) {
  const mainRef = useRef<HTMLElement>(null);
  const { scrolled, hidden } = useScrollDirection(mainRef);
  const { pathname } = useLocation();
  const headerless = isUnitDetailPath(pathname);
  const tabbarless = isTabbarless(pathname);

  return (
    <div
      className="relative flex flex-col overflow-hidden rounded-[44px] bg-bg"
      style={{
        width: 390,
        height: 844,
        // CSS spec: any non-`none` transform makes an element the containing block
        // for `position:fixed` descendants. Without this, sticky CTAs / bottom sheets /
        // toasts inside the frame would attach to the browser viewport and visually
        // fly out of the iPhone mockup on desktop preview.
        transform: 'translateZ(0)',
        boxShadow:
          '0 0 0 1px rgba(255,255,255,0.06), 0 0 0 12px #0a0a0a, 0 0 0 13px rgba(255,255,255,0.08), 0 50px 100px -20px rgba(0,0,0,0.7), 0 30px 60px -30px rgba(0,0,0,0.6)',
      }}
    >
      {/* Dynamic Island mock */}
      <div className="pointer-events-none absolute left-1/2 top-2 z-50 h-[28px] w-[120px] -translate-x-1/2 rounded-full bg-black" />

      {/* Status bar mock */}
      <div className="relative z-10 flex h-11 shrink-0 items-end justify-between px-7 pb-1 text-[14px] font-semibold text-label">
        <span className="tracking-tight">9:41</span>
        <span className="flex items-center gap-1.5">
          <svg width="17" height="11" viewBox="0 0 17 11" fill="currentColor" aria-hidden>
            <rect x="0" y="7" width="3" height="4" rx="0.5" />
            <rect x="4.5" y="5" width="3" height="6" rx="0.5" />
            <rect x="9" y="2.5" width="3" height="8.5" rx="0.5" />
            <rect x="13.5" y="0" width="3" height="11" rx="0.5" />
          </svg>
          <svg width="15" height="11" viewBox="0 0 15 11" fill="currentColor" aria-hidden>
            <path d="M7.5 0C4.4 0 1.6 1.2 -.4 3.2L7.5 11l7.9-7.8C13.4 1.2 10.6 0 7.5 0Z" />
          </svg>
          <svg width="25" height="11" viewBox="0 0 25 11" fill="none" aria-hidden>
            <rect x="0.5" y="0.5" width="21" height="10" rx="2.5" stroke="currentColor" opacity="0.4" />
            <rect x="2" y="2" width="18" height="7" rx="1.5" fill="currentColor" />
            <rect x="22.5" y="3.5" width="1.5" height="4" rx="0.75" fill="currentColor" opacity="0.4" />
          </svg>
        </span>
      </div>

      <CollapsingHeader scrolled={scrolled} hidden={hidden || headerless} />

      <main
        ref={mainRef}
        className="relative flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"
      >
        {children}
      </main>

      {!tabbarless && <TabBar />}
      <ToastHost />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  Native shell (mobile)                                          */
/* ────────────────────────────────────────────────────────────── */

function NativeShell({ children }: { children: ReactNode }) {
  const mainRef = useRef<HTMLElement>(null);
  const { scrolled, hidden } = useScrollDirection(mainRef);
  const { pathname } = useLocation();
  const headerless = isUnitDetailPath(pathname);
  const tabbarless = isTabbarless(pathname);

  return (
    <div className="flex h-[100dvh] flex-col">
      <CollapsingHeader scrolled={scrolled} hidden={hidden || headerless} />
      <main
        ref={mainRef}
        className="flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"
      >
        {children}
      </main>
      {!tabbarless && <TabBar />}
      <ToastHost />
    </div>
  );
}
